export const CHART_CENTER = { x: 430, y: 300 } as const;
export const CHART_RADIUS = 220;
export const LABEL_RADIUS = 248;
export const GRID_TICKS = [20, 40, 60, 80, 100] as const;

interface TooltipRectangle {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function getMetricTooltipPosition(
  anchor: TooltipRectangle,
  area: TooltipRectangle,
  tooltip: Pick<TooltipRectangle, 'width' | 'height'>,
) {
  const inset = 8;
  const anchorTop = anchor.top - area.top;
  // Axis order is clockwise, not top-to-bottom: Speed is high on the left,
  // while Trajectory is low on the right. Use actual available space instead.
  const placeBelow = anchorTop + anchor.height / 2 < area.height / 2;
  const preferredTop = placeBelow
    ? anchorTop + anchor.height + inset
    : anchorTop - tooltip.height - inset;

  return {
    left: Math.max(
      inset,
      Math.min(
        area.width - tooltip.width - inset,
        anchor.left - area.left + (anchor.width - tooltip.width) / 2,
      ),
    ),
    top: Math.max(
      inset,
      Math.min(area.height - tooltip.height - inset, preferredTop),
    ),
  };
}

export function getAxisAngle(index: number, axisCount: number) {
  return -Math.PI / 2 + (index * Math.PI * 2) / axisCount;
}

export function getRadialPoint(
  index: number,
  axisCount: number,
  value: number,
  radius = CHART_RADIUS,
) {
  const angle = getAxisAngle(index, axisCount);
  const distance = (radius * value) / 100;
  return {
    x: CHART_CENTER.x + Math.cos(angle) * distance,
    y: CHART_CENTER.y + Math.sin(angle) * distance,
  };
}

export function getPolygonPoints(
  scores: readonly number[],
  radius = CHART_RADIUS,
) {
  return scores
    .map((score, index) => {
      const point = getRadialPoint(index, scores.length, score, radius);
      return `${point.x},${point.y}`;
    })
    .join(' ');
}

function nearestPointOnSegment(
  point: { x: number; y: number },
  start: { x: number; y: number },
  end: { x: number; y: number },
) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return {
      distance: Math.hypot(point.x - start.x, point.y - start.y),
      ratio: 0,
    };
  }
  const ratio = Math.max(
    0,
    Math.min(
      1,
      ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared,
    ),
  );
  return {
    distance: Math.hypot(
      point.x - (start.x + ratio * dx),
      point.y - (start.y + ratio * dy),
    ),
    ratio,
  };
}

export function getNearestSeriesId<
  T extends { id: string; scores: readonly number[] },
>(
  series: readonly T[],
  point: { x: number; y: number },
  maxDistance = 12,
  radius = CHART_RADIUS,
) {
  const candidates: { distance: number; score: number; id: string }[] = [];
  for (const item of series) {
    if (item.scores.length === 0) continue;
    const vertices = item.scores.map((score, index) =>
      getRadialPoint(index, item.scores.length, score, radius),
    );
    let nearestForSeries: { distance: number; score: number } | null = null;
    for (let index = 0; index < vertices.length; index += 1) {
      const nearest = nearestPointOnSegment(
        point,
        vertices[index],
        vertices[(index + 1) % vertices.length],
      );
      if (!nearestForSeries || nearest.distance < nearestForSeries.distance) {
        nearestForSeries = {
          distance: nearest.distance,
          score:
            item.scores[index] * (1 - nearest.ratio) +
            item.scores[(index + 1) % item.scores.length] * nearest.ratio,
        };
      }
    }
    if (nearestForSeries && nearestForSeries.distance <= maxDistance) {
      candidates.push({ ...nearestForSeries, id: item.id });
    }
  }
  if (candidates.length === 0) return null;
  const byDirectHit = (
    a: (typeof candidates)[number],
    b: (typeof candidates)[number],
  ) => a.distance - b.distance || a.score - b.score || a.id.localeCompare(b.id);
  candidates.sort(byDirectHit);
  const nearest = candidates[0];
  // At the current 10px caller tolerance these are about 2px and 4.5px:
  // direct strokes stay precise while close shapes resolve locally.
  if (nearest.distance <= maxDistance * 0.2) return nearest.id;
  const ambiguous = candidates.filter(
    (candidate) => candidate.distance <= nearest.distance + maxDistance * 0.45,
  );
  ambiguous.sort((a, b) => a.score - b.score || byDirectHit(a, b));
  return ambiguous[0].id;
}

export interface SeriesInteractionState {
  focusedId: string | null;
  pinnedId: string | null;
  pointerId: string | null;
  suppressedId: string | null;
}

export function getInteractionActiveId(state: SeriesInteractionState) {
  const pointerId =
    state.pointerId === state.suppressedId ? null : state.pointerId;
  const focusedId =
    state.focusedId === state.suppressedId ? null : state.focusedId;
  return pointerId ?? focusedId ?? state.pinnedId;
}

export function togglePinnedSeries(
  state: SeriesInteractionState,
  id: string,
): SeriesInteractionState {
  return state.pinnedId === id
    ? { ...state, pinnedId: null, suppressedId: id }
    : { ...state, pinnedId: id, suppressedId: null };
}

export function setInteractionPreview(
  state: SeriesInteractionState,
  channel: 'focusedId' | 'pointerId',
  id: string | null,
): SeriesInteractionState {
  const nextState = {
    ...state,
    [channel]: id,
  };
  // Suppress only the uninterrupted hover/focus left behind by unpinning.
  // Once both have left, a fresh interaction with that model can preview again.
  if (
    nextState.pointerId !== state.suppressedId &&
    nextState.focusedId !== state.suppressedId
  ) {
    nextState.suppressedId = null;
  }
  return nextState;
}
